import { isValidUsStateAbbrev, normalizeUsState } from './usStates';

// Database field length constraints based on SQLAlchemy models
export const FIELD_LIMITS = {
  // Provider fields
  provider: {
    first_name: 50,
    last_name: 50,
    npi_number: 20,
    contract_number: 7,
    specialty: 100,
    phone: 20,
    email: 100,
    address_line1: 100,
    address_line2: 100,
    city: 50,
    county: 50,
    state: 2,
    zip_code: 10,
    password: 255
  },
  
  // Client fields
  client: {
    first_name: 50,
    first_initial: 1,
    last_name: 50,
    gender: 20,
    phone: 20,
    email: 100,
    password: 50,
    address_line1: 100,
    address_line2: 100,
    city: 50,
    county: 50,
    state: 2,
    zip_code: 10,
    emergency_contact_name: 100,
    emergency_contact_phone: 20,
    allergies: 1000,
    notes: 1000,
    isp: 500
  },
  
  // Visit fields
  visit: {
    status: 20,
    ratio: 5,
    service_code: 20,
    unusual_incident_injury: 300,
    notes: 65535, // TEXT field
    behavior_notes: 65535, // TEXT field
    unusual_incident_documentation: 65535, // TEXT field
    unusual_incident_actions_taken: 65535, // TEXT field
    unusual_incident_cause: 65535 // TEXT field
  },
  
  // Service fields
  service: {
    state: 20,
    county: 50,
    municipality: 50,
    subregion: 50,
    program: 10,
    service_unit: 25,
    category: 20,
    service_title: 255,
    PAWS_roll_up: 20,
    code: 5,
    description: 255
  },
  
  // User fields
  user: {
    user_email: 255,
    password_hash: 255,
    last_login_ip: 45
  }
};

// Validation functions
export const validateFieldLength = (value: string, maxLength: number): boolean => {
  if (!value) return true; // Empty values are allowed unless field is required
  return value.length <= maxLength;
};

export const validateRequiredField = (value: string, maxLength: number): boolean => {
  if (!value || value.trim() === '') return false; // Required fields cannot be empty
  return value.length <= maxLength;
};

export const getFieldError = (value: string, maxLength: number, fieldName: string, isRequired: boolean = false): string | null => {
  if (isRequired && (!value || value.trim() === '')) {
    return `${fieldName} is required`;
  }
  
  if (value && value.length > maxLength) {
    return `${fieldName} cannot exceed ${maxLength} characters (current: ${value.length})`;
  }
  
  return null;
};

// Specific validation functions for common field types
export const validateEmail = (email: string): string | null => {
  if (!email) return null; // Empty emails are allowed unless required
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return 'Please enter a valid email address';
  }
  
  return getFieldError(email, FIELD_LIMITS.provider.email, 'Email');
};

export const validatePhone = (phone: string): string | null => {
  if (!phone) return null; // Empty phones are allowed unless required
  
  // Remove all non-digit characters for validation
  const digitsOnly = phone.replace(/\D/g, '');
  if (digitsOnly.length < 10 || digitsOnly.length > 15) {
    return 'Please enter a valid phone number (10-15 digits)';
  }
  
  return getFieldError(phone, FIELD_LIMITS.provider.phone, 'Phone');
};

export const validatePassword = (password: string): string | null => {
  if (!password) return null; // Empty passwords are allowed unless required
  
  if (password.length < 8) {
    return 'Password must be at least 8 characters long';
  }
  
  return getFieldError(password, FIELD_LIMITS.provider.password, 'Password');
};

export const validateZipCode = (zipCode: string): string | null => {
  if (!zipCode) return null; // Empty zip codes are allowed unless required
  
  const zipRegex = /^\d{5}(-\d{4})?$/;
  if (!zipRegex.test(zipCode)) {
    return 'Please enter a valid ZIP code (e.g., 12345 or 12345-6789)';
  }
  
  return getFieldError(zipCode, FIELD_LIMITS.provider.zip_code, 'ZIP Code');
};

export const validateState = (state: string): string | null => {
  if (!state) return null; // Empty states are allowed unless required

  const normalized = normalizeUsState(state);
  if (!isValidUsStateAbbrev(normalized) || !normalized) {
    return 'State must be a 2-letter US abbreviation (e.g. OH, NV)';
  }

  return getFieldError(normalized, FIELD_LIMITS.provider.state, 'State');
};

export const validateNPI = (npi: string): string | null => {
  if (!npi) return null; // Empty NPI is allowed unless required
  
  const npiRegex = /^\d{10}$/;
  if (!npiRegex.test(npi)) {
    return 'NPI must be exactly 10 digits';
  }
  
  return getFieldError(npi, FIELD_LIMITS.provider.npi_number, 'NPI Number');
};

export const validateContractNumber = (contractNumber: string): string | null => {
  if (!contractNumber) return null; // Empty contract number is allowed unless required
  
  const contractRegex = /^\d{7}$/;
  if (!contractRegex.test(contractNumber)) {
    return 'Contract number must be exactly 7 digits';
  }
  
  return getFieldError(contractNumber, FIELD_LIMITS.provider.contract_number, 'Contract Number');
};

// Date validation functions for visits
export const validateVisitDates = (startDate: string, endDate: string): string | null => {
  if (!startDate || !endDate) {
    return null; // Let required field validation handle empty values
  }
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // Check if start date is after end date
  if (start >= end) {
    return 'Start date must be before end date';
  }
  
  // Check if dates are more than 24 hours apart
  const diffMs = end.getTime() - start.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);
  
  if (diffHours > 24) {
    return 'Visit duration cannot exceed 24 hours';
  }
  
  return null;
};

export const validateStartDate = (startDate: string, endDate: string): string | null => {
  if (!startDate) return null; // Let required field validation handle empty values
  
  if (!endDate) return null; // Can't validate without end date
  
  return validateVisitDates(startDate, endDate);
};

export const validateEndDate = (startDate: string, endDate: string): string | null => {
  if (!endDate) return null; // Let required field validation handle empty values
  
  if (!startDate) return null; // Can't validate without start date
  
  return validateVisitDates(startDate, endDate);
};

// Helper function to get field limit
export const getFieldLimit = (entity: keyof typeof FIELD_LIMITS, field: string): number => {
  return FIELD_LIMITS[entity][field as keyof typeof FIELD_LIMITS[typeof entity]] || 255;
};

// Validation hook for real-time validation
export const useFieldValidation = (entity: keyof typeof FIELD_LIMITS, field: string, isRequired: boolean = false) => {
  const maxLength = getFieldLimit(entity, field);
  
  const validate = (value: string): string | null => {
    if (isRequired) {
      return getFieldError(value, maxLength, field, true);
    }
    return getFieldError(value, maxLength, field, false);
  };
  
  const isValid = (value: string): boolean => {
    return validate(value) === null;
  };
  
  return {
    validate,
    isValid,
    maxLength,
    fieldName: field
  };
};
